import config from '../config';

// Cache for tracks to avoid repeated API calls
let tracksCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Generate a URL-friendly slug from a track name
 */
export function generateTrackSlug(trackName) {
  if (!trackName) return '';
  
  return trackName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim('-'); // Remove leading/trailing hyphens
}

/**
 * Get track configuration from API with caching
 */
export async function getTracks() {
  // Check cache first
  const now = Date.now();
  if (tracksCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    return tracksCache;
  }

  try {
    const response = await fetch(`${config.api.url}admin/tracks?format=json`);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    const tracks = data.tracks || ['Track 1', 'Track 2', 'Track 3'];
    
    // Create track objects with names and slugs
    const trackObjects = tracks.map((name, index) => ({
      id: `track${index + 1}`,
      name: name,
      slug: generateTrackSlug(name),
      index: index + 1
    }));
    
    // Update cache
    tracksCache = trackObjects;
    cacheTimestamp = now;
    
    return trackObjects;
    
  } catch (error) {
    console.error('Error fetching tracks:', error);
    
    // Return default tracks if API fails
    const defaultTracks = [
      { id: 'track1', name: 'Smart Charging', slug: 'smart-charging', index: 1 },
      { id: 'track2', name: 'Tailings Treatment', slug: 'tailings-treatment', index: 2 },
      { id: 'track3', name: 'Bitumen Viscosity', slug: 'bitumen-viscosity', index: 3 }
    ];
    
    tracksCache = defaultTracks;
    cacheTimestamp = now;
    
    return defaultTracks;
  }
}

/**
 * Get track by slug
 */
export async function getTrackBySlug(slug) {
  const tracks = await getTracks();
  return tracks.find(track => track.slug === slug);
}

/**
 * Get track by ID
 */
export async function getTrackById(id) {
  const tracks = await getTracks();
  return tracks.find(track => track.id === id);
}

/**
 * Clear tracks cache (useful for admin updates)
 */
export function clearTracksCache() {
  tracksCache = null;
  cacheTimestamp = null;
}

/**
 * Get tracks for dropdown options (submit page)
 */
export async function getTracksForSubmit() {
  const tracks = await getTracks();
  return tracks.map(track => ({
    value: track.name,  // Use track name as value instead of track.id
    label: track.name
  }));
}

/**
 * Get tracks for dropdown options (ask page - includes general)
 */
export async function getTracksForAsk() {
  const tracks = await getTracks();
  const options = [
    { value: 'general', label: 'General' }
  ];
  
  tracks.forEach(track => {
    options.push({
      value: track.id,
      label: track.name
    });
  });
  
  return options;
}
